import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
    padding: '20px',
  },
  header: {
    color: '#e94560',
    fontSize: '22px',
    fontWeight: 'bold',
    marginBottom: '4px',
    marginTop: '20px',
  },
  subtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '32px',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '16px',
  },
  label: {
    color: '#a0a0b0',
    fontSize: '13px',
    marginBottom: '8px',
    display: 'block',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '16px',
    marginBottom: '20px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '16px',
    marginBottom: '20px',
    boxSizing: 'border-box',
    outline: 'none',
    appearance: 'none',
    colorScheme: 'dark',
  },
  gameButton: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#0f3460',
    border: '2px solid #0f3460',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '16px',
    marginBottom: '12px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  gameButtonSelected: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#0f3460',
    border: '2px solid #e94560',
    borderRadius: '10px',
    color: '#e94560',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '12px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  startButton: {
    width: '100%',
    padding: '20px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '16px',
  },
  disabledButton: {
    width: '100%',
    padding: '20px',
    backgroundColor: '#444',
    border: 'none',
    borderRadius: '12px',
    color: '#888',
    fontSize: '20px',
    fontWeight: 'bold',
    marginTop: '16px',
    boxSizing: 'border-box',
    display: 'block',
  },
  dealerBadge: {
    backgroundColor: '#0f3460',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '24px',
  },
  dealerName: {
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: 'bold',
  },
  dealerPayroll: {
    color: '#a0a0b0',
    fontSize: '13px',
    marginTop: '4px',
  },
  back: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '16px',
    cursor: 'pointer',
  },
  // Show ~6 games, scroll for the rest.
  gameList: {
    maxHeight: '384px',
    overflowY: 'auto',
    marginRight: '-8px',
    paddingRight: '8px',
  },
  selectedGameBadge: {
    backgroundColor: '#0f3460',
    border: '2px solid #e94560',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '24px',
    color: '#e94560',
    fontSize: '16px',
    fontWeight: 'bold',
  },
};

styles.benchmarkBadge = {
  marginLeft: '10px',
  padding: '2px 8px',
  borderRadius: '5px',
  backgroundColor: '#e94560',
  color: '#ffffff',
  fontSize: '11px',
  fontWeight: 'bold',
  letterSpacing: '1px',
  verticalAlign: 'middle',
};
styles.benchmarkNotice = {
  backgroundColor: 'rgba(233, 69, 96, 0.15)',
  border: '1px solid #e94560',
  borderRadius: '12px',
  color: '#ffffff',
  fontSize: '15px',
  padding: '14px 16px',
  marginBottom: '16px',
  lineHeight: 1.4,
};

// Height of one game button (padding + font + margin) — 6 visible before scroll.
const GAME_ROW_PX = 64;

function StartSession() {
  const [gameTypes, setGameTypes] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [tableNumber, setTableNumber] = useState('');
  const [targetRounds, setTargetRounds] = useState('');
  // Pit, table minimum and type of test come from lists the casino sets up. A
  // list left empty means that question is left off this screen entirely.
  const [floorOptions, setFloorOptions] = useState({});
  const [pit, setPit] = useState(localStorage.getItem('pit') || '');
  const [tableMin, setTableMin] = useState(localStorage.getItem('table_min') || '');
  const [testType, setTestType] = useState(localStorage.getItem('test_type') || '');
  // What the game itself asks about the table: how it shuffles, how many decks, the
  // style of game, which side bets. These are not notes on the test - a dealer is
  // measured against the benchmark for the table they were actually standing at.
  const [gameVars, setGameVars] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('game'); // 'game' -> 'details'
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const dealer = JSON.parse(localStorage.getItem('dealer') || '{}');

  useEffect(() => {
    fetch(`${API}/game-types?floor=1`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setGameTypes(data);
      });

  }, [token]);

  // The lists depend on the game, so they are fetched once one is chosen.
  useEffect(() => {
    if (!selectedGame) return;
    const kind = encodeURIComponent(selectedGame.kind || '');
    fetch(`${API}/floor-options?game_kind=${kind}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setFloorOptions(d || {});
        // Keep last night's answers where they still exist, so an observer sets
        // the pit once a shift rather than once a test.
        const keep = (list, value) => ((list || []).some(o => o.label === value) ? value : '');
        setPit(prev => keep(d.pit, prev));
        setTableMin(prev => keep(d.table_min, prev));
        setTestType(prev => keep(d.test_type, prev));
        setGameVars(() => {
          const next = {};
          (selectedGame.session_variables || []).forEach(k => {
            next[k] = keep(d[k], localStorage.getItem(k) || '');
          });
          return next;
        });
      })
      .catch(() => {});
  }, [selectedGame, token]);

  // What each game variable is called on screen.
  const VAR_LABELS = {
    shuffle_type: 'Shuffle Type',
    deck_count: 'Number of Decks',
    game_style: 'Game Style',
    side_bet_type: 'Side Bet',
  };
  const gameVariables = (selectedGame?.session_variables || [])
    .filter(k => (floorOptions[k] || []).length > 0);

  function goToDetails() {
    // Benchmark tests need a minimum number of rounds; pre-fill the target to it.
    const min = selectedGame?.benchmark_mode ? selectedGame.min_benchmark_rounds : null;
    if (min && (!targetRounds || parseInt(targetRounds) < min)) setTargetRounds(String(min));
    setStep('details');
  }

  async function startSession() {
    if (!selectedGame) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dealer_id: dealer.id,
          game_type_id: selectedGame.id,
          pit: pit || null,
          table_min: tableMin || null,
          test_type: testType || null,
          shuffle_type: gameVars.shuffle_type || null,
          deck_count: gameVars.deck_count || null,
          game_style: gameVars.game_style || null,
          side_bet_type: gameVars.side_bet_type || null,
          table_number: tableNumber || null,
          target_rounds: targetRounds ? parseInt(targetRounds) : null,
        }),
      });
      const data = await res.json();
      localStorage.setItem('session_id', data.session_id);
      localStorage.setItem('game_type', JSON.stringify(selectedGame));
      localStorage.setItem('target_rounds', targetRounds || '0');
      localStorage.setItem('table_number', tableNumber || '');
      localStorage.setItem('pit', pit || '');
      localStorage.setItem('table_min', tableMin || '');
      localStorage.setItem('test_type', testType || '');
      Object.entries(gameVars).forEach(([k, v]) => localStorage.setItem(k, v || ''));
      localStorage.setItem('round_number', '1');
      navigate('/survey');
    } catch (e) {
      alert('Could not start session');
    }
    setLoading(false);
  }

  return (
    <div style={styles.container}>
      <div
        style={styles.back}
        onClick={() => (step === 'details' ? setStep('game') : navigate('/select-dealer'))}
      >
        ← Back
      </div>
      <div style={styles.header}>Start Session</div>
      <div style={styles.subtitle}>
        {step === 'game' ? 'Choose a game' : 'Session details'}
      </div>

      <div style={styles.dealerBadge}>
        <div style={styles.dealerName}>
          {dealer.first_name} {dealer.last_name}
        </div>
        <div style={styles.dealerPayroll}>ID: {dealer.payroll_number}</div>
      </div>

      {step === 'game' && (
        <>
          <div style={styles.card}>
            <label style={styles.label}>Game Type</label>
            <div style={{
              ...styles.gameList,
              maxHeight: gameTypes.length > 6 ? `${GAME_ROW_PX * 6}px` : 'none',
            }}>
              {gameTypes.map(game => (
                <button
                  key={game.id}
                  style={selectedGame?.id === game.id ? styles.gameButtonSelected : styles.gameButton}
                  onClick={() => setSelectedGame(game)}
                >
                  {game.name}
                  {game.benchmark_mode && <span style={styles.benchmarkBadge}>BENCHMARK</span>}
                </button>
              ))}
            </div>
          </div>

          {selectedGame ? (
            <button style={styles.startButton} onClick={goToDetails}>
              Next
            </button>
          ) : (
            <button disabled style={styles.disabledButton}>Select a game type</button>
          )}
        </>
      )}

      {step === 'details' && (
        <>
          <div style={styles.selectedGameBadge}>{selectedGame?.name}</div>

          {selectedGame?.benchmark_mode && (
            <div style={styles.benchmarkNotice}>
              This test counts toward the benchmark. Minimum {selectedGame.min_benchmark_rounds} rounds.
            </div>
          )}

          <div style={styles.card}>
            {gameVariables.map(key => (
              <React.Fragment key={key}>
                <label style={styles.label} htmlFor={key}>{VAR_LABELS[key] || key}</label>
                <select
                  id={key}
                  style={styles.select}
                  value={gameVars[key] || ''}
                  onChange={e => setGameVars(prev => ({ ...prev, [key]: e.target.value }))}
                >
                  <option value="">Not recorded</option>
                  {(floorOptions[key] || []).map(o => (
                    <option key={o.id} value={o.label}>{o.label}</option>
                  ))}
                </select>
              </React.Fragment>
            ))}

            {(floorOptions.pit || []).length > 0 && (
              <>
                <label style={styles.label} htmlFor="pit">Pit</label>
                <select id="pit" style={styles.select} value={pit} onChange={e => setPit(e.target.value)}>
                  <option value="">Not recorded</option>
                  {floorOptions.pit.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
                </select>
              </>
            )}

            {(floorOptions.table_min || []).length > 0 && (
              <>
                <label style={styles.label} htmlFor="table-min">Table Minimum</label>
                <select id="table-min" style={styles.select} value={tableMin}
                        onChange={e => setTableMin(e.target.value)}>
                  <option value="">Not recorded</option>
                  {floorOptions.table_min.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
                </select>
              </>
            )}

            {(floorOptions.test_type || []).length > 0 && (
              <>
                <label style={styles.label} htmlFor="test-type">Type of Test</label>
                <select id="test-type" style={styles.select} value={testType}
                        onChange={e => setTestType(e.target.value)}>
                  <option value="">Not recorded</option>
                  {floorOptions.test_type.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
                </select>
              </>
            )}

            <label style={styles.label}>Table Number (optional)</label>
            <input
              style={styles.input}
              type="text"
              value={tableNumber}
              onChange={e => setTableNumber(e.target.value)}
              placeholder="e.g. Table 7"
            />
            <label style={styles.label}>Target Rounds (optional)</label>
            <input
              style={styles.input}
              type="number"
              value={targetRounds}
              onChange={e => setTargetRounds(e.target.value)}
              placeholder="e.g. 20"
            />
          </div>

          <button style={styles.startButton} onClick={startSession} disabled={loading}>
            {loading ? 'Starting...' : 'Start Observation'}
          </button>
        </>
      )}
    </div>
  );
}

export default StartSession;